import { PROMPT_STORAGE_KEY, PROMPT_TTL_MS } from "../../features/common/constants";



export function extractPromptDataFromUrl(searchString) {
    if(!searchString) return null;
    const params = new URLSearchParams(searchString);
    const prompt = params.get("prompt")
    const trimmedPrompt = prompt && prompt.trim().length > 0 ? prompt.trim() : null;

    const rawKeys = params.get('keys');
    const rawSingleKey = params.get('key');

    let keys = [];
    if (rawKeys) {
    keys = rawKeys
      .split(',')
      .map((k) => k.trim())
      .filter(Boolean);
    } else if (rawSingleKey && rawSingleKey.trim().length > 0) {
      keys = [rawSingleKey.trim()];
    }

   // If neither prompt nor keys exist, return null
  if (!trimmedPrompt && keys.length === 0) {
    return null;
  }
  return {prompt: trimmedPrompt, keys};
}

export function savePendingPrompt(data) {
  if (!data) return;
  const payload = { 
    prompt: data.prompt || null,
    keys: Array.isArray(data?.keys) ? data.keys : [],
    timestamp: Date.now(),
  };
  try {
    localStorage.setItem(PROMPT_STORAGE_KEY, JSON.stringify(payload));
  } catch (error) {
    console.error('Failed to save prompt to localStorage:', error);
  }
}

export function getPendingPrompt() {
  try {
    const rawData = localStorage.getItem(PROMPT_STORAGE_KEY);
    if (!rawData) return null;
    const data = JSON.parse(rawData);
    const isExpired = Date.now() - data.timestamp > PROMPT_TTL_MS;
    if (isExpired) {
      clearPendingPrompt();
      return null;
    }
    return {
      prompt: data.prompt,
      keys: Array.isArray(data.keys) ? data.keys : [],
    };
  } catch (error) {
    console.error('Failed to parse prompt from localStorage:', error);
    clearPendingPrompt();
    return null;
  }
}


export function clearPendingPrompt() {
  try {
    localStorage.removeItem(PROMPT_STORAGE_KEY);
  } catch (error) {
    console.error('Failed to remove prompt from localStorage:', error);
  }
}

export function consumePendingPrompt() {
  const prompt = getPendingPrompt();
  if (prompt) {
    clearPendingPrompt();
  }
  return prompt;
}



/**
 * Generates a clean, meaningful, and collision-free project name from a prompt.
 * 
 * Rules enforced:
 * - Removes conversational noise ("build a", "create an", "i want to", etc.)
 * - Title Cases core keywords
 * - Enforces DrapCode validation: only alphanumeric characters and spaces
 * - Strict length limit: Max 50 chars (including unique suffix)
 * - Guarantees 100% collision uniqueness using a short Base-36 entropy tag
 * 
 * @param {string} prompt
 * @returns {string} e.g. "Doctor Appointment Booking 8f2a"
 */
export function generateMeaningfulProjectName(prompt) {
  // 1. Generate a short 4-character unique suffix (Base-36 of timestamp + random)
  // Example: "8k2m", "3x9a" (Collision probability is practically zero per user)
  const uniqueEntropy = (Date.now().toString(36).slice(-2) + Math.random().toString(36).slice(-2)).toLowerCase();

  const fallbackName = `AI Project ${uniqueEntropy}`;

  if (!prompt || typeof prompt !== 'string' || prompt.trim().length === 0) {
    return fallbackName;
  }

  // 2. Conversational Stop Words & Common Intent Fillers to remove
  const fillerPatterns = [
    /^(can you\s+)?(please\s+)?(help me\s+)?(build|create|make|develop|design|generate|code|set up)(\s+(an?|the|some))?/i,
    /^(i want( to)?|i need( to)?|let's|lets)(\s+(build|create|make|develop))?(\s+(an?|the|some))?/i,
    /^(a|an|the)\s+/i,
  ];

  let cleaned = prompt.trim();
  fillerPatterns.forEach((pattern) => {
    cleaned = cleaned.replace(pattern, '').trim();
  });

  // 3. Keep ONLY alphanumeric characters and spaces (Strict DrapCode regex requirement)
  cleaned = cleaned.replace(/[^a-zA-Z0-9\s]/g, ' ').replace(/\s+/g, ' ').trim();

  if (!cleaned) {
    return fallbackName;
  }

  // 4. Extract first 3-4 significant words and Title Case them
  const words = cleaned.split(' ').slice(0, 4);
  const titleCased = words
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');

  // 5. Ensure entire name + suffix stays well within 50 characters
  // Suffix = " " + 4 chars = 5 chars total
  const maxTitleLength = 50 - (uniqueEntropy.length + 1); // 45 chars
  const truncatedTitle = titleCased.length > maxTitleLength 
    ? titleCased.substring(0, maxTitleLength).trim() 
    : titleCased;

  return `${truncatedTitle} ${uniqueEntropy}`;
}
